import interstellarApi from './interstellarApi';

export interface FreeSwitchGroupAccount {
  id: number;
  name: string;
  cosmos_account_id: string;
}

export interface FreeSwitchGroup {
  id: number;
  name: string;
  freeswitch_id: string;
  freeswitch_uri: string;
  created_at: string;
  modified_at: string;
  account_count: number;
  accounts: FreeSwitchGroupAccount[];
  wizard_enabled: boolean;
  max_accounts: number;
  available_slots: number;
}

export interface FreeSwitchGroupListResponse {
  groups: FreeSwitchGroup[];
  total: number;
}

/** Payload for creating a new FreeSwitchGroup */
export interface CreateFreeSwitchGroupPayload {
  name: string;
  instance_type?: 't3.large' | 'c5.large' | 'c5.xlarge' | 'c5.2xlarge';
  wizard_enabled?: boolean;
  max_accounts?: number;
}

/** Payload for updating a FreeSwitchGroup */
export interface UpdateFreeSwitchGroupPayload {
  wizard_enabled?: boolean;
  max_accounts?: number;
}

/** Response after updating a FreeSwitchGroup */
export interface UpdateFreeSwitchGroupResponse {
  id: number;
  name: string;
  wizard_enabled: boolean;
  max_accounts: number;
}

/** Response after creating a FreeSwitchGroup */
export interface CreateFreeSwitchGroupResponse {
  group_id: number;
  name: string;
  stack_id: string;
  stack_name: string;
  status: string;
  console_url: string;
}

/** Status of a FreeSwitchGroup's CloudFormation stack */
export interface FreeSwitchGroupStatusResponse {
  group_id: number;
  name: string;
  stack_id: string;
  stack_name: string;
  status: string;
  status_reason: string | null;
  in_progress: boolean;
  succeeded: boolean;
  failed: boolean;
  freeswitch_uri: string | null;
  freeswitch_id: string | null;
  instance_id: string | null;
  instance_private_ip: string | null;
  efs_access_point_id: string | null;
  failure_reasons: string[];
  console_url: string;
}

/** Allowed instance types for shared FreeSWITCH groups */
export const ALLOWED_SHARED_INSTANCE_TYPES = [
  { value: 't3.large', label: 't3.large (default)' },
  { value: 'c5.large', label: 'c5.large' },
  { value: 'c5.xlarge', label: 'c5.xlarge' },
  { value: 'c5.2xlarge', label: 'c5.2xlarge' },
] as const;

/**
 * Fetches all FreeSwitchGroups (shared FreeSWITCH instances) and their member accounts.
 * @see interstellar/services/infrastructure_service/endpoints.py — `list_freeswitch_groups`
 */
export const fetchFreeSwitchGroups = async (): Promise<FreeSwitchGroupListResponse> => {
  const response = await interstellarApi.get<FreeSwitchGroupListResponse>(
    '/infrastructure/freeswitch-groups'
  );
  return response.data;
};

/**
 * Creates a new FreeSwitchGroup by deploying a CloudFormation stack.
 * @see interstellar/services/infrastructure_service/endpoints.py — `create_freeswitch_group`
 */
export const createFreeSwitchGroup = async (
  payload: CreateFreeSwitchGroupPayload
): Promise<CreateFreeSwitchGroupResponse> => {
  const response = await interstellarApi.post<CreateFreeSwitchGroupResponse>(
    '/infrastructure/freeswitch-groups',
    payload
  );
  return response.data;
};

/**
 * Gets the status of a FreeSwitchGroup's CloudFormation stack.
 * Use this to poll for deployment completion.
 */
export const fetchFreeSwitchGroupStatus = async (
  groupId: number
): Promise<FreeSwitchGroupStatusResponse> => {
  const response = await interstellarApi.get<FreeSwitchGroupStatusResponse>(
    `/infrastructure/freeswitch-groups/${groupId}/status`
  );
  return response.data;
};

/**
 * Fetches only the groups that are ready to accept new accounts.
 * Filters out groups with pending status.
 * @deprecated Use fetchWizardReadyGroups for the wizard
 */
export const fetchReadyFreeSwitchGroups = async (): Promise<FreeSwitchGroup[]> => {
  const response = await fetchFreeSwitchGroups();
  // Filter out groups that are still being provisioned (URI would be 'pending')
  return response.groups.filter(
    (group) => group.freeswitch_uri && group.freeswitch_uri !== 'pending'
  );
};

/**
 * Fetches groups available for the account creation wizard.
 * Returns only groups that are:
 * - Enabled for the wizard (wizard_enabled=true)
 * - Not pending provisioning
 * - Have available capacity (account_count < max_accounts)
 */
export const fetchWizardReadyGroups = async (): Promise<FreeSwitchGroup[]> => {
  const response = await interstellarApi.get<FreeSwitchGroupListResponse>(
    '/infrastructure/freeswitch-groups',
    { params: { for_wizard: true } }
  );
  return response.data.groups;
};

/**
 * Updates a FreeSwitchGroup's wizard settings.
 */
export const updateFreeSwitchGroup = async (
  groupId: number,
  payload: UpdateFreeSwitchGroupPayload
): Promise<UpdateFreeSwitchGroupResponse> => {
  const response = await interstellarApi.patch<UpdateFreeSwitchGroupResponse>(
    `/infrastructure/freeswitch-groups/${groupId}`,
    payload
  );
  return response.data;
};
